import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Box,
  Avatar,
  Typography,
  Paper,
  Button,
  Container,
  Input,
  Rating,
  IconButton,
  Tooltip,
  Dialog,
} from "@mui/material"
import SharieSkeleton from "../UI/SharieSkeleton"
import { useApiService } from "../hooks/Api/useApiService"
import { useLocation } from "react-router-dom"
import styles from "./ProfileCard.module.css"
import { useUser } from "../hooks/Contexts/UserContext/UserContext"
import useDeviceType from "../hooks/Utils/useDeviceType"
import InventoryIcon from "@mui/icons-material/Inventory"
import EmailModal from "../Modals/EmailModal/EmailModal"
import CheckIcon from "@mui/icons-material/Check"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Legend,
  Cell,
  CartesianGrid,
} from "recharts"
import Spinner from "../UI/Spinner/Spinner"
import Icn from "../UI/SharieIcn/SharieIcn"
import SharieLegend from "../UI/SharieLegend"
import { useNavigate } from "react-router-dom"
import CaptureVideoImage from "../hooks/Utils/CaptureVideoImage"
import WallpaperIcon from "@mui/icons-material/Wallpaper"
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import CloseIcon from "@mui/icons-material/Close"
import T from "../hooks/Language/T"
import LanguageToggle from "../UI/LanguageToggle"
import useTranslationSharie from "../hooks/Language/useTranslation"
import babyuser from "../../assets/images/babyuser.png"
import childuser from "../../assets/images/childuser.png"
import teenuser from "../../assets/images/teenuser.png"
import useError from "../hooks/Error/useError"
import adultuser from "../../assets/images/adultuser.png"

const ProfileCard = React.memo((props) => {
  const [business, setBusiness] = useState(null)
  const [fullscreenImg, setFullscreenImg] = useState(false)
  const [is_compressing, setIsCompressing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [input_value, setInputValue] = useState("")
  const [should_fetch, setShouldFetch] = useState(false)
  const [edit_able, setEditable] = useState(null)
  const [is_following, setIsFollowing] = useState(false)
  const [is_cover, setIsCover] = useState(false)
  const [image, setImage] = useState(null)
  const [cover_image, setCoverImage] = useState(null)
  const [user, setUser] = useState(null)
  const [preferences, setPreferences] = useState([])
  const [rank, setRank] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [next_level, setNextLevel] = useState(50)
  const deviceType = useDeviceType()
  const isDesktop = deviceType === "desktop"
  const { Error: AlertError, error, success } = useError()

  const toggleFlip = () => {
    setIsFlipped((prev) => !prev)
  }

  const default_preferences = [
    { word: "", times: 0 },
    { word: "", times: 0 },
    { word: "", times: 0 },
  ]

  const { postData } = useApiService()
  const location = useLocation()
  const { context_user, updateUser } = useUser()
  const user_name = props.user_name
  const emailRef = useRef()
  const navigate = useNavigate()
  const t = useTranslationSharie()
  const colorPalette = ["#90CAF9", "#A5D6A7", "#FFCC80", "#CE93D8", "#EF9A9A"]
  const guest = localStorage.getItem("guest") === "true"

  useEffect(() => {
    postData("users/getuserrank", { user_name: user_name || localStorage.getItem("user") }).then(
      (data) => {
        setRank(data?.rank)
      }
    )
  }, [isFlipped, should_fetch])

  useEffect(() => {
    setEditable(location.state?.editable)

    postData("users/getuser", { user_name: user_name || localStorage.getItem("user") })
      .then((data) => {
        if (data?.user?.length > 0) {
          updateUser(data?.user[0])
          setUser(data?.user[0])
          setEditable(data?.user[0].user_name === localStorage.getItem("user"))

          if (location.pathname === "/home") setEditable(false)
          setLoading(false)
        }
        setInputValue(context_user?.email)
        return postData("business/getbusinessbyuser", {
          user_name: user_name || localStorage.getItem("user"),
        })
      })
      .then((data) => {
        setBusiness(data?.business)
      })
      .catch((error) => {
        error(error.message)
      })
  }, [should_fetch, user_name, location.state?.editable])

  useEffect(() => {
    postData("sharie/getisfollowing", {
      follower: localStorage.getItem("user"),
      user_name: user_name,
    }).then((data) => {
      setIsFollowing(data?.following)
    })

    postData("sharie/getpreferences", {
      user_name: user_name || localStorage.getItem("user"),
    }).then((data) => {
      setPreferences(data?.skills)
    })
  }, [user_name, is_following, should_fetch])

  useEffect(() => {
    // Set the next level based on the current rank
    if (rank >= 1 && rank <= 50) {
      setNextLevel(50)
    } else if (rank > 50 && rank <= 500) {
      setNextLevel(500)
    } else if (rank > 500 && rank <= 1500) {
      setNextLevel(1500)
    } else {
      setNextLevel(50) // Default value if rank doesn't match any case
    }
  }, [rank])

  const colorsForBars = useMemo(() => {
    const shuffled = [...colorPalette]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled.slice(0, 5) // top five bars
  }, [])

  const openCaptureFile = (isCover) => {
    setIsCover(isCover)
    setOpen(!open)
    // Set the flag to differentiate between image and cover
  }
  const closeCaptureFile = () => {
    setOpen(false) // Close the component
  }
  const isVideo = () => {
    const mimeType = cover_image?.split(";")[0] // Get the MIME type
    return mimeType?.startsWith("data:video/")
  }

  const handleEdit = () => {
    setEditing(true)
  }
  const handleSaveUser = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(input_value)) {
      error("invalid email format!")
      return
    }

    setEditing(false)

    postData("users/updateuser", {
      email: input_value,
      user_name: context_user?.user_name,
      image: image || context_user?.image,
      cover: cover_image || context_user?.cover,
    })
      .then((data) => {
        setShouldFetch(!should_fetch)
      })
      .catch((error) => {
        error(error.message)
      })
    setOpen(false)
  }
  const handleInputChange = (e) => {
    setInputValue(e.target.value)
  }
  const clearInput = () => {
    setInputValue("")
  }
  const openEmailModal = () => {
    if (emailRef.current) {
      emailRef.current.childFunction()
    }
  }

  const handleFollow = () => {
    postData("sharie/addfollower", {
      follower: localStorage.getItem("user"),
      user_name: user_name,
    }).then((data) => {})
    setIsFollowing(true)
  }
  const handleunFollow = () => {
    postData("sharie/removefollower", {
      follower: localStorage.getItem("user"),
      user_name: user_name,
    }).then((data) => {})
    setIsFollowing(false)
  }

  function getTopFiveWords(wordsArray) {
    return wordsArray
      .sort((a, b) => b.times - a.times) // Sort in descending order
      .slice(0, 5) // Take the first 5 elements
  }
  const handleGetSomeCredit = () => {
    navigate("/buycredit")
  }
  const formatYAxis = (value) => {
    return value % 1 === 0 ? value : "" // Only show the value if it's a whole number
  }
  const getRankImage = () => {
    switch (true) {
      case rank >= 1 && rank <= 50:
        updateUser({ ...context_user, rank: rank, rank_image: babyuser })
        return babyuser
      case rank > 50 && rank <= 500:
        updateUser({ ...context_user, rank: rank, rank_image: childuser })
        return childuser
      case rank > 500 && rank <= 1500:
        updateUser({ ...context_user, rank: rank, rank_image: teenuser })
        return teenuser
      case rank > 1500 && rank <= 3000:
        updateUser({ ...context_user, rank: rank, rank_image: adultuser })
        return teenuser
      // case rank > 100 && rank <= 500:
      //   return adultuser
      // case rank > 500 && rank <= 1000:
      //   return herouser

      default:
        updateUser({ ...context_user, rank: rank, rank_image: babyuser })
        return babyuser
    }
  }
  return (
    <T>
      <div
        className={
          isDesktop ? styles.profile_page_container_desktop : styles.profile_page_container_mobile
        }
      >
        <EmailModal ref={emailRef} />
        {loading ? (
          <Box sx={{ p: 4 }}>
            <Typography variant='h6' color='inherit'>
              {t("Loading content...")}
            </Typography>
            <SharieSkeleton variant='rectangular' width={"300px"} height={"600px"} />
          </Box>
        ) : (
          <div
            className={
              isDesktop ? styles.profile_container_desktop : styles.profile_container_mobile
            }
            style={{ height: "100%" }}
          >
            <Container style={{ display: "flex", justifyContent: "center" }}>
              <Paper className={styles.mui_paper}>
                {/* Top-right corner button */}
                <Box
                  style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}
                >
                  {edit_able && (
                    <Box className={styles.language_toggle}>
                      <LanguageToggle />
                    </Box>
                  )}
                  {user_name && !is_following && localStorage.getItem("user") !== user_name ? (
                    <Box className={styles.follow_box}>
                      <Button
                        variant='contained'
                        color='secondary'
                        size='small'
                        onClick={handleFollow}
                        disabled={guest}
                      >
                        Create Link
                      </Button>
                    </Box>
                  ) : (
                    is_following && (
                      <Box className={styles.follow_box}>
                        <Button
                          variant='contained'
                          color='secondary'
                          size='small'
                          onClick={handleunFollow}
                          disabled={guest}
                        >
                          Remove Link
                        </Button>
                        <IconButton aria-label='check icon' sx={{ cursor: "default" }}>
                          <CheckIcon />
                        </IconButton>
                      </Box>
                    )
                  )}
                </Box>
                {/* Profile Section */}
                <Box className={styles.profile_section}>
                  {/* Centered Profile Picture */}
                  <Box
                    style={{
                      background: !isVideo()
                        ? `url(${
                            cover_image || user.cover_image || context_user?.cover
                          }) no-repeat center center`
                        : "",
                      backgroundSize: !isVideo() ? "cover" : "auto",
                      width: "100%",
                      height: "auto",
                      position: "relative", // Make sure the parent has relative positioning
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {/* Fullscreen Icon */}
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        color: "white",
                        zIndex: 10,
                      }}
                      onClick={() => setFullscreenImg(true)} // Toggle fullscreen on click
                    >
                      <FullscreenIcon />
                    </IconButton>
                    <Dialog
                      open={fullscreenImg} // Controlled by state
                      onClose={() => setFullscreenImg(false)} // Close dialog
                      maxWidth='xl'
                      fullScreen
                      sx={{ backgroundColor: "black" }}
                    >
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          color: "white",
                          background: "rgba(0,0,0,0.5)",
                          zIndex: 10,
                        }}
                        onClick={() => setFullscreenImg(false)} // Close fullscreen dialog
                      >
                        <CloseIcon />
                      </IconButton>

                      <Box
                        sx={{
                          width: "100vw",
                          height: "100vh",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {isVideo() ? (
                          <video autoPlay muted loop controls width='100%'>
                            <source
                              src={user.cover_image || context_user?.cover || cover_image}
                              type='video/mp4'
                            />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <img
                            src={user.cover_image || context_user?.cover || cover_image}
                            alt='Fullscreen'
                            style={{
                              width: "100vw",
                              height: "100vh",
                              objectFit: "contain",
                            }}
                          />
                        )}
                      </Box>
                    </Dialog>
                    {/* Render video if the uploaded file is a video */}
                    {isVideo() && (
                      <video
                        autoPlay
                        loop
                        muted
                        style={{
                          objectFit: "cover",
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <source
                          src={cover_image || user.cover_image || context_user?.cover}
                          type='video/mp4'
                        />
                        Your browser does not support the video tag.
                      </video>
                    )}
                    {/* Profile Image and rank*/}
                    <div
                      className={styles.flipContainer}
                      onMouseEnter={(e) => {
                        setIsFlipped(true)
                      }}
                      onMouseLeave={() => setIsFlipped(false)}
                      onClick={toggleFlip} // This makes it work on mobile!
                    >
                      <div className={`${styles.flipper} ${isFlipped ? styles.hover : ""}`}>
                        {isFlipped ? (
                          <Tooltip title={rank?.toString() + " / " + next_level?.toString()}>
                            <Avatar alt='Back' src={getRankImage()} className={styles.avatar} />
                          </Tooltip>
                        ) : (
                          <Avatar
                            alt='Front'
                            src={user.image || context_user?.image}
                            className={styles.avatar}
                          />
                        )}
                      </div>
                    </div>
                  </Box>
                  {editing && edit_able && (
                    <>
                      <div className={styles.capture_container}>
                        <Icn
                          tooltip={"Profile Image"}
                          color={"inherit"}
                          onClick={() => {
                            openCaptureFile(false)
                          }}
                          icon={<FaceRetouchingNaturalIcon />}
                        />
                        <Icn
                          tooltip={"Cover Image"}
                          color={"inherit"}
                          onClick={() => {
                            openCaptureFile(true)
                          }}
                          icon={<WallpaperIcon />}
                        />
                      </div>
                      {open && (
                        <CaptureVideoImage
                          setIsCompressing={setIsCompressing}
                          image={is_cover ? cover_image : image}
                          setImage={is_cover ? setCoverImage : setImage}
                          open={open}
                          onClose={closeCaptureFile} // Pass a close function if needed
                        />
                      )}
                      {is_compressing && <Spinner />}
                    </>
                  )}
                  {/* Centered User Information */}
                  {!editing && (
                    <Typography variant='h5' className={styles.centered_text}>
                      {user?.user_name || context_user?.user_name}
                    </Typography>
                  )}
                  {!editing ? (
                    <Typography
                      variant='subtitle1'
                      color='text.secondary'
                      className={styles.centered_text}
                    >
                      {user.email || context_user?.email}
                    </Typography>
                  ) : (
                    <Box className={styles.editable_box}>
                      <Input
                        defaultValue={input_value}
                        onClick={clearInput}
                        onChange={handleInputChange}
                      />
                      <Button onClick={handleSaveUser} disabled={guest}>
                        Save
                      </Button>
                      <Button color={"secondary"} onClick={() => setEditing(false)}>
                        Cancel
                      </Button>
                      <AlertError />
                    </Box>
                  )}
                </Box>
                {/* User Info */}
                {!editing && (
                  <>
                    <Box className={styles.chart_container}>
                      <ResponsiveContainer width='100%' height={"100%"}>
                        <BarChart
                          data={
                            preferences?.length > 0
                              ? getTopFiveWords(preferences)
                              : default_preferences
                          }
                          margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray='3 3' />
                          <XAxis
                            dataKey='word'
                            tick={{ fontSize: 12, fill: "black" }} // Set text color to black
                          />
                          <YAxis
                            tick={{ fontSize: 12, fill: "black" }}
                            tickFormatter={formatYAxis} // Apply the new formatter to Y-axis
                          />
                          <Bar dataKey='times' barSize={10}>
                            {preferences?.length > 0
                              ? getTopFiveWords(preferences).map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={colorsForBars[index % colorsForBars.length]}
                                  />
                                ))
                              : default_preferences.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={colorsForBars[index % colorsForBars.length]}
                                  />
                                ))}
                          </Bar>
                          <Legend content={<SharieLegend />} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* Details */}

                    <div className={styles.bottom_row_container}>
                      <div className={styles.bottom_column_container}>
                        <Typography variant='subtitle2' color='text.secondary'>
                          Credit
                        </Typography>
                        {business?.status === "paid" ? (
                          <Tooltip arrow title={user?.credit || context_user?.credit}>
                            <IconButton aria-label='credit icon' sx={{ cursor: "default" }}>
                              <Typography variant='h6' color='black'>
                                Unlimited
                              </Typography>
                            </IconButton>
                          </Tooltip>
                        ) : context_user?.credit === 0 ? (
                          <Tooltip arrow title={"Get Credit"}>
                            <Button
                              variant='outlined'
                              color='primary'
                              onClick={handleGetSomeCredit}
                            >
                              Get credit now
                            </Button>
                          </Tooltip>
                        ) : (
                          <Typography variant='h6' color='black'>
                            {user?.credit || context_user?.credit}
                          </Typography>
                        )}
                      </div>
                      <div className={styles.bottom_column_container}>
                        <Typography variant='subtitle2' color='text.secondary'>
                          Help
                        </Typography>
                        <Icn
                          tooltip={"Support"}
                          color={"#1976d2"}
                          onClick={openEmailModal}
                          icon={<InventoryIcon />}
                        />
                      </div>
                      <div className={styles.bottom_column_container}>
                        <Typography variant='subtitle2' color='text.secondary'>
                          Rating
                        </Typography>

                        <Rating
                          sx={{ marginBlockEnd: "0.5rem" }}
                          name='user-rating'
                          value={Number(context_user?.rating?.toFixed(1)) || 0}
                          precision={1}
                          readOnly
                        />
                      </div>
                    </div>
                  </>
                )}
                {/* Edit Button */}
                {!editing && edit_able ? (
                  <Box className={styles.edit_button} sx={{ width: "100%" }}>
                    <Button
                      disabled={guest}
                      variant='contained'
                      color='primary'
                      fullWidth
                      onClick={handleEdit}
                    >
                      {t("Edit Profile")}
                    </Button>
                  </Box>
                ) : (
                  <></>
                )}
              </Paper>
            </Container>
          </div>
        )}
      </div>
    </T>
  )
})

export default ProfileCard
